import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Link as LinkIcon, Monitor } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BannerAdsTab = ({ bannerAds, onRefresh }) => {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newAd, setNewAd] = useState({ 
    name: '', 
    iframe_url: '', 
    position: '',
    width: 300,
    height: 250
  });
  const [loading, setLoading] = useState(false);

  const handleAddAd = async (e) => {
    e.preventDefault();
    if (!newAd.name.trim() || !newAd.iframe_url.trim() || !newAd.position) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    
    try {
      await axios.post(`${API}/admin/banner-ads`, {
        name: newAd.name.trim(),
        iframe_url: newAd.iframe_url.trim(),
        position: newAd.position,
        width: parseInt(newAd.width),
        height: parseInt(newAd.height)
      });
      
      toast.success('Banner ad added successfully!');
      setNewAd({ name: '', iframe_url: '', position: '', width: 300, height: 250 });
      setShowAddDialog(false);
      onRefresh();
    } catch (error) {
      console.error('Error adding banner ad:', error);
      toast.error(error.response?.data?.detail || 'Failed to add banner ad');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAd = async (adId) => {
    if (!window.confirm('Are you sure you want to delete this banner ad?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/banner-ads/${adId}`);
      toast.success('Banner ad deleted successfully');
      onRefresh();
    } catch (error) {
      console.error('Error deleting banner ad:', error);
      toast.error('Failed to delete banner ad');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-brand font-bold text-white">Banner Ad Management</h2>
        
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="btn-hover bg-gradient-to-r from-green-600 to-blue-600 text-white" data-testid="add-banner-ad-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Banner Ad (300x250)
            </Button>
          </DialogTrigger>
          
          <DialogContent className="glass-dark border-white/20 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white">Add New Banner Ad</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleAddAd} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="banner-name" className="text-white">Ad Name</Label>
                <Input
                  id="banner-name"
                  type="text"
                  value={newAd.name}
                  onChange={(e) => setNewAd(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter ad name (e.g., Homepage Sidebar Ad)"
                  className="form-input text-white"
                  required
                  data-testid="banner-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="iframe-url" className="text-white">Iframe URL</Label>
                <div className="relative">
                  <LinkIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/50" />
                  <Input
                    id="iframe-url"
                    type="url"
                    value={newAd.iframe_url}
                    onChange={(e) => setNewAd(prev => ({ ...prev, iframe_url: e.target.value }))}
                    placeholder="https://example.com/banner-ad-iframe"
                    className="pl-10 form-input text-white"
                    required
                    data-testid="iframe-url-input"
                  />
                </div>
                <p className="text-xs text-white/60">
                  Enter the iframe URL for your banner advertisement
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="banner-width" className="text-white">Width (px)</Label>
                  <Input
                    id="banner-width"
                    type="number"
                    value={newAd.width}
                    onChange={(e) => setNewAd(prev => ({ ...prev, width: e.target.value }))}
                    min="100"
                    max="1000"
                    className="form-input text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="banner-height" className="text-white">Height (px)</Label>
                  <Input
                    id="banner-height"
                    type="number"
                    value={newAd.height}
                    onChange={(e) => setNewAd(prev => ({ ...prev, height: e.target.value }))}
                    min="100"
                    max="1000"
                    className="form-input text-white"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="banner-position" className="text-white">Display Position</Label>
                <Select value={newAd.position} onValueChange={(value) => setNewAd(prev => ({ ...prev, position: value }))}>
                  <SelectTrigger className="form-input text-white" data-testid="banner-position-select">
                    <SelectValue placeholder="Select display position" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-white/20">
                    <SelectItem value="homepage" className="text-white hover:bg-white/10">
                      Homepage Sidebar
                    </SelectItem>
                    <SelectItem value="video-page" className="text-white hover:bg-white/10">
                      Video Page Sidebar
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex space-x-3">
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 btn-hover bg-gradient-to-r from-green-600 to-blue-600 text-white"
                  data-testid="submit-banner-ad"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="loading-spinner mr-2"></div>
                      Adding...
                    </div>
                  ) : (
                    'Add Banner Ad'
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddDialog(false)}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      
      {/* Banner Ads List */}
      <div className="space-y-4">
        {bannerAds.length === 0 ? (
          <Card className="glass-dark border-white/20">
            <CardContent className="p-12 text-center">
              <div className="text-6xl mb-4">🖼️</div>
              <h3 className="text-xl font-semibold mb-2 text-white">No Banner Ads Yet</h3>
              <p className="text-white/70 mb-4">
                Add 300x250 banner advertisements to display on your homepage and video pages.
              </p>
              <Button
                onClick={() => setShowAddDialog(true)}
                className="btn-hover bg-gradient-to-r from-green-600 to-blue-600 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Banner Ad
              </Button>
            </CardContent>
          </Card>
        ) : (
          bannerAds.map((ad) => (
            <BannerAdItem key={ad.id} ad={ad} onDelete={() => handleDeleteAd(ad.id)} />
          ))
        )}
      </div>
    </div>
  );
};

const BannerAdItem = ({ ad, onDelete }) => {
  const getPositionColor = (position) => {
    switch (position) {
      case 'homepage': return 'from-blue-500 to-cyan-500';
      case 'video-page': return 'from-purple-500 to-pink-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getPositionLabel = (position) => {
    switch (position) {
      case 'homepage': return 'Homepage Sidebar';
      case 'video-page': return 'Video Page Sidebar';
      default: return position;
    }
  };

  return (
    <Card className="glass-dark border-white/20 card-hover">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0 mr-6">
            <div className="flex items-center space-x-3 mb-3">
              <div className={`p-2 bg-gradient-to-r ${getPositionColor(ad.position)} rounded-lg`}>
                <Monitor className="h-4 w-4 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">{ad.name}</h3>
                <div className="flex items-center space-x-3">
                  <Badge className={`bg-gradient-to-r ${getPositionColor(ad.position)} text-white border-none`}>
                    {getPositionLabel(ad.position)}
                  </Badge>
                  <Badge variant="outline" className="border-green-500/30 text-green-300">
                    {ad.width}x{ad.height}
                  </Badge>
                  <Badge 
                    variant={ad.is_active ? 'default' : 'secondary'} 
                    className={ad.is_active ? 'bg-green-500/20 text-green-300 border-green-500/30' : 'bg-gray-500/20 text-gray-400'}
                  >
                    {ad.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <div>
                <span className="text-sm text-white/70">Iframe URL:</span>
                <a 
                  href={ad.iframe_url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="block text-sm text-purple-400 hover:text-purple-300 truncate mt-1"
                >
                  {ad.iframe_url}
                </a>
              </div>
              
              <div className="flex items-center space-x-4 text-xs text-white/60">
                <span>Created: {new Date(ad.created_at).toLocaleDateString()}</span>
                <span>Size: {ad.width}x{ad.height}px</span>
              </div>
            </div>
          </div>
          
          {/* Delete Button */}
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              data-testid={`delete-banner-ad-${ad.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default BannerAdsTab;